/**
 * Cognito authentication — the same pool and flow as the chat UI.
 *
 * Uses SRP so the password is never sent to Cognito in the clear. The access token
 * carries `cognito:groups`, and the SMS API re-resolves entitlement from the
 * verified token server-side — so this `isStaff` only decides what the console
 * *offers*, never what it is allowed to fetch.
 */

import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import type { AppConfig } from "./config";

export interface Identity {
  accessToken: string;
  email: string;
  /** Display name from the token's `name` claim, if the account has one set. */
  name?: string;
  /** True when the token carries a staff group. */
  isStaff: boolean;
  groups: string[];
  expiresAt: number;
}

/**
 * Must match STAFF_GROUPS in agents/registry.py and the interceptor. Drives what
 * the console offers, not what it may fetch — the API recomputes this from the
 * verified token, so a disagreement is a cosmetic UI bug, never an escalation.
 */
const STAFF_GROUPS = new Set(["advisor", "advisors", "student-advisor", "staff", "registry"]);

function pool(config: AppConfig): CognitoUserPool {
  return new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.clientId });
}

function identityFromSession(session: CognitoUserSession, fallbackEmail: string): Identity {
  const accessToken = session.getAccessToken();
  const payload = accessToken.decodePayload() as Record<string, unknown>;

  const idPayload = session.getIdToken()?.decodePayload() as
    | Record<string, unknown>
    | undefined;
  const email =
    typeof idPayload?.email === "string" && idPayload.email ? idPayload.email : fallbackEmail;
  const name =
    typeof idPayload?.name === "string" && idPayload.name ? idPayload.name : undefined;

  const raw = payload["cognito:groups"];
  const groups = Array.isArray(raw) ? raw.map(String) : [];
  const isStaff = groups.some((g) => STAFF_GROUPS.has(g.toLowerCase()));

  return {
    accessToken: accessToken.getJwtToken(),
    email,
    name,
    isStaff,
    groups,
    expiresAt: accessToken.getExpiration() * 1000,
  };
}

export class NewPasswordRequired extends Error {
  constructor(readonly user: CognitoUser) {
    super("A new password is required for this account.");
  }
}

export function signIn(config: AppConfig, email: string, password: string): Promise<Identity> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool(config) });
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(identityFromSession(session, email)),
        onFailure: (err) => reject(new Error(err.message ?? "Sign-in failed.")),
        newPasswordRequired: () => reject(new NewPasswordRequired(user)),
      },
    );
  });
}

export function completeNewPassword(
  user: CognitoUser,
  email: string,
  newPassword: string,
): Promise<Identity> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(identityFromSession(session, email)),
      onFailure: (err) => reject(new Error(err.message ?? "Could not set the password.")),
    });
  });
}

export function currentIdentity(config: AppConfig): Promise<Identity | null> {
  return new Promise((resolve) => {
    const user = pool(config).getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      resolve(identityFromSession(session, user.getUsername()));
    });
  });
}

export function signOut(config: AppConfig): void {
  pool(config).getCurrentUser()?.signOut();
}

// --- Microsoft Entra staff sign-in (hosted UI, authorization-code + PKCE) -------
//
// Staff sign in through the same Cognito hosted UI as the chat app, jumping
// straight to Entra. This is a public client so there is no secret in the browser;
// PKCE binds the authorization code to this device. The tokens that come back are
// cached into amazon-cognito-identity-js via setSignInUserSession, so the rest of
// the console — currentIdentity(), refresh, signOut() — treats an Entra session
// exactly like a native one. Kept in sync with the chat app's frontend/src/lib/auth.ts.

const VERIFIER_KEY = "edu.pkce.verifier";
const STATE_KEY = "edu.pkce.state";

/** True when the deployment has Entra federation wired (see the SMS stack). */
export function entraConfigured(config: AppConfig): boolean {
  return Boolean(config.cognitoDomain && config.entraProvider);
}

/** The registered redirect URI: this app's origin. Must be in the client's callbackUrls. */
function redirectUri(): string {
  return `${window.location.origin}/`;
}

function randomUrlSafe(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64Url(buffer.buffer);
}

function base64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(digest);
}

/** Begin the redirect to Cognito's hosted UI, jumping straight to Entra. */
export async function startEntraSignIn(config: AppConfig): Promise<void> {
  if (!entraConfigured(config)) {
    throw new Error("Entra sign-in is not configured for this deployment.");
  }
  const verifier = randomUrlSafe(48);
  const state = randomUrlSafe(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const url = new URL(`${config.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("redirect_uri", redirectUri());
  url.searchParams.set("scope", "openid email profile");
  // Skip Cognito's provider-picker and go straight to Entra.
  url.searchParams.set("identity_provider", config.entraProvider as string);
  url.searchParams.set("code_challenge", await pkceChallenge(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", state);
  window.location.assign(url.toString());
}

/** Strip ?code/&state from the address bar so a refresh does not re-run the exchange. */
function clearAuthParamsFromUrl(): void {
  window.history.replaceState({}, document.title, redirectUri());
}

/**
 * If the page loaded on a hosted-UI redirect (`?code=...`), exchange the code for
 * tokens and return the resulting identity. Returns null when there is no code to
 * process, so App can call it unconditionally on boot.
 */
export async function completeHostedUiSignIn(config: AppConfig): Promise<Identity | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code) return null;

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  clearAuthParamsFromUrl();

  if (!verifier || params.get("state") !== expectedState) {
    // No verifier or a state mismatch: not a flow we started. Fail closed.
    throw new Error("Sign-in could not be verified. Please try again.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) {
    throw new Error("Entra sign-in could not be completed.");
  }
  const tokens = (await response.json()) as {
    access_token: string;
    id_token: string;
    refresh_token: string;
  };

  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
  });

  // Persist through the SDK so getCurrentUser()/refresh/signOut work uniformly.
  const payload = session.getAccessToken().decodePayload() as Record<string, unknown>;
  const username = typeof payload.username === "string" ? payload.username : String(payload.sub);
  const user = new CognitoUser({ Username: username, Pool: pool(config) });
  user.setSignInUserSession(session);

  return identityFromSession(session, "");
}
